/**
 * SIMPLIX DIGITAL - INTERACTIVE JAVASCRIPT CONTROLLER
 * High-performance vanilla JS controller with multi-step business configurator,
 * scrollspy active navigation, FAQ physics, consultation modal, and micro-interactions.
 */

import './style.css';

// Master Business Database for Configurator
const BIZ_PROFILES = {
  restaurant: {
    icon: '🍽️',
    name: 'Restaurant / Café',
    title: 'Restaurant & Café Operations Suite',
    subtitle: 'From paper menus to zero-latency table orders & automated kitchen tickets.',
    defaultGoals: ['online', 'billing', 'whatsapp', 'inventory'],
    services: [
      { name: 'Mobile-First Website & Digital QR Menu', desc: 'Instant item & price updates with zero reprint costs' },
      { name: 'Direct WhatsApp Table Ordering', desc: 'Customers order directly without waiting for busy staff' },
      { name: 'Rapid POS & Thermal KOT Routing', desc: 'Prints tickets straight to kitchen or bar counter' },
      { name: 'Ingredient & Stock Threshold Alerts', desc: 'Know before essentials run out during dinner rush' },
      { name: 'Customer Database & Repeat Offers', desc: 'Re-engage past diners with automated WhatsApp promos' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '5–8 Working Days',
    timeSaved: '~18 hrs/week manual work saved'
  },
  retail: {
    icon: '🛍️',
    name: 'Retail Shop',
    title: 'Retail Fast Checkout & Stock Suite',
    subtitle: 'Barcode scanning, variant tracking, GST invoices and instant digital WhatsApp catalogs.',
    defaultGoals: ['billing', 'inventory', 'online', 'reports'],
    services: [
      { name: 'Touchscreen POS & Barcode Scanner', desc: 'Rapid billing with auto-generated UPI payment QRs' },
      { name: 'Size & Variant Inventory Tracking', desc: 'Track items across sizes, colors, brands & batches' },
      { name: 'Digital WhatsApp Product Catalog', desc: 'Share your latest collection via link with direct checkout' },
      { name: 'Automated Daily Sales & GST Reports', desc: 'One-click reconciliation for your accountant' },
      { name: 'Low Stock Auto-Alerts', desc: 'Prevent out-of-stock items before weekend rush' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '5–7 Working Days',
    timeSaved: '~15 hrs/week manual work saved'
  },
  salon: {
    icon: '✂️',
    name: 'Salon / Spa / Clinic',
    title: 'Salon & Spa Digital Booking Engine',
    subtitle: 'Eliminate receptionist phone queues and drastically cut no-shows with automated reminders.',
    defaultGoals: ['online', 'enquiries', 'customers', 'whatsapp'],
    services: [
      { name: '24/7 Web Appointment Portal', desc: 'Clients pick available time slots and service specialists' },
      { name: 'Automated WhatsApp Reminders', desc: 'Cuts no-shows by sending confirmations 3 hours prior' },
      { name: 'Staff Schedule & Chair Calendar', desc: 'Prevents double booking and organizes stylist shifts' },
      { name: 'Client Treatment & History Records', desc: 'Quick reference for previous preferences & notes' },
      { name: 'Google Review Collection Workflow', desc: 'Auto-requests 5-star ratings after completed visits' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '4–7 Working Days',
    timeSaved: '~12 hrs/week manual work saved'
  },
  service: {
    icon: '🛠️',
    name: 'Service Business',
    title: 'Local Service & Contractor Growth Suite',
    subtitle: 'Make your business discoverable on Google and capture every high-intent local inquiry.',
    defaultGoals: ['online', 'enquiries', 'whatsapp', 'customers'],
    services: [
      { name: 'High-Trust Conversion Website', desc: 'Showcases your past work, credentials, and customer reviews' },
      { name: 'Google Business Profile & Local SEO', desc: 'Rank higher when local customers search for your trade' },
      { name: 'Instant WhatsApp Quote Request', desc: 'Inquiries routed directly to your mobile phone via WhatsApp' },
      { name: 'Automated Inquiry Acknowledgement', desc: 'Instant message reassuring client you will call back' },
      { name: 'Professional Domain & Business Email', desc: 'you@yourbusiness.com setup with secure fast hosting' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '4–6 Working Days',
    timeSaved: '~10 hrs/week manual work saved'
  },
  office: {
    icon: '🏢',
    name: 'Small Office',
    title: 'Small Office & Internal Workflow Automation',
    subtitle: 'Eliminate manual data copying between spreadsheets, emails, and invoicing software.',
    defaultGoals: ['automation', 'reports', 'custom', 'customers'],
    services: [
      { name: 'Custom Operational Dashboard', desc: 'Track client deliverables, statuses, and team tasks' },
      { name: 'Automated PDF Invoicing & Receipts', desc: 'Generates professional invoices automatically on milestone' },
      { name: 'Multi-App Database Synchronization', desc: 'Syncs web leads to Google Sheets, Notion or Airtable' },
      { name: 'Role-Based Access Control', desc: 'Secure logins for staff, managers, and accounting' },
      { name: 'Custom API Integrations', desc: 'Connects your existing software without manual copy-pasting' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '7–12 Working Days',
    timeSaved: '~20 hrs/week manual work saved'
  },
  other: {
    icon: '🌐',
    name: 'Other Venture',
    title: 'Tailored Business Digital Setup',
    subtitle: 'Practical technology designed around your specific operating model.',
    defaultGoals: ['online', 'enquiries', 'automation', 'custom'],
    services: [
      { name: 'High-Performance Modern Website', desc: 'Clean, fast, mobile-first design built to convert' },
      { name: 'Custom Inquiry & Lead Pipeline', desc: 'Connects website forms to WhatsApp and spreadsheets' },
      { name: 'Operational Workflow Optimization', desc: 'Replaces manual paperwork with automated logic' },
      { name: 'Documentation & Setup Assistance', desc: 'Technical guidance for domains, payments & compliance' },
      { name: 'Ongoing Dedicated Support', desc: 'Direct support for questions, tweaks, and upgrades' }
    ],
    starterPrice: 'From ₹4,999',
    growthPrice: 'From ₹9,999',
    customPrice: 'Custom Quote',
    timeline: '5–10 Working Days',
    timeSaved: '~14 hrs/week manual work saved'
  }
};

// Goals Dictionary
const GOAL_LABELS = {
  online: 'Get online',
  enquiries: 'Increase enquiries',
  billing: 'Manage billing',
  inventory: 'Track inventory',
  customers: 'Manage customers',
  automation: 'Automate repetitive work',
  reports: 'Create reports',
  custom: 'Build custom software'
};

document.addEventListener('DOMContentLoaded', () => {
  initNavbarScroll();
  initScrollspy();
  initMobileMenu();
  initMultiStepConfigurator();
  initFaqAccordion();
  initModalHandling();
  initSmoothScroll();
});

/**
 * Sticky Navbar Scroll Effect
 */
function initNavbarScroll() {
  const header = document.getElementById('main-header');
  if (!header) return;

  const handleScroll = () => {
    if (window.scrollY > 20) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
}

/**
 * Scrollspy for Desktop Navigation
 */
function initScrollspy() {
  const sections = document.querySelectorAll('section[id]');
  const navLinks = document.querySelectorAll('.nav-link');

  if (!sections.length || !navLinks.length) return;

  const onScroll = () => {
    const scrollY = window.pageYOffset;

    sections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      const sectionTop = section.offsetTop - 120;
      const sectionId = section.getAttribute('id');

      if (scrollY > sectionTop && scrollY <= sectionTop + sectionHeight) {
        navLinks.forEach(link => {
          link.classList.remove('active');
          if (link.getAttribute('href') === `#${sectionId}`) {
            link.classList.add('active');
          }
        });
      }
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
}

/**
 * Mobile Navigation Drawer
 */
function initMobileMenu() {
  const toggleBtn = document.getElementById('mobile-menu-btn');
  const menu = document.getElementById('mobile-menu');
  const links = document.querySelectorAll('.mobile-nav-link');

  if (!toggleBtn || !menu) return;

  toggleBtn.addEventListener('click', () => {
    const isOpen = !menu.classList.contains('hidden');
    if (isOpen) {
      menu.classList.add('hidden');
    } else {
      menu.classList.remove('hidden');
    }
  });

  links.forEach(link => {
    link.addEventListener('click', () => {
      menu.classList.add('hidden');
    });
  });
}

/**
 * 3-Step Interactive Business Needs Configurator
 */
function initMultiStepConfigurator() {
  const state = {
    type: 'restaurant',
    goals: new Set(['online', 'billing', 'inventory', 'whatsapp']),
    scale: 'small'
  };

  const typeButtons = document.querySelectorAll('.config-type-btn');
  const goalButtons = document.querySelectorAll('.config-goal-btn');
  const scaleButtons = document.querySelectorAll('.config-scale-btn');
  const resultContainer = document.getElementById('config-result-panel');

  if (!resultContainer) return;

  // Calculate recommendation package and pricing based on selections
  function calculateRecommendation() {
    const profile = BIZ_PROFILES[state.type] || BIZ_PROFILES.restaurant;
    const hasComplexGoals = state.goals.has('custom') || state.goals.has('automation');
    const hasPosOrInventory = state.goals.has('billing') || state.goals.has('inventory') || state.goals.has('reports');
    const isMultiLocation = state.scale === 'multi';

    let recommendedTier = 'Business Growth';
    let priceLabel = profile.growthPrice;
    let badgeText = 'Recommended Starting Setup';

    if (isMultiLocation || (hasComplexGoals && state.scale === 'growing')) {
      recommendedTier = 'Custom Business System';
      priceLabel = 'Custom Quote';
      badgeText = 'Scalable Multi-Location Setup';
    } else if (!hasPosOrInventory && !hasComplexGoals && (state.scale === 'starting' || state.goals.size <= 2)) {
      recommendedTier = 'Business Starter';
      priceLabel = profile.starterPrice;
      badgeText = 'Fast Turnaround Starter';
    } else {
      recommendedTier = 'Business Growth';
      priceLabel = profile.growthPrice;
      badgeText = 'Most Popular for Local Growth';
    }

    return {
      profile,
      recommendedTier,
      priceLabel,
      badgeText
    };
  }

  // Render the Right-Hand Result Preview
  function renderResult() {
    const { profile, recommendedTier, priceLabel, badgeText } = calculateRecommendation();

    // Map active goals to badges
    const activeGoalsList = Array.from(state.goals).map(g => GOAL_LABELS[g] || g);
    const goalsPills = activeGoalsList.map(g => `
      <span class="inline-flex items-center gap-1 text-[11px] font-medium bg-slate-800 text-emerald-400 px-2.5 py-1 rounded-md border border-slate-700">
        ✓ ${g}
      </span>
    `).join('');

    const servicesHtml = profile.services.map((item, idx) => `
      <div class="flex items-start gap-3 p-3 rounded-xl bg-slate-800/80 border border-slate-700/80 hover:border-emerald-500/40 transition-colors">
        <div class="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
          ✓
        </div>
        <div>
          <div class="text-xs font-bold text-white">${item.name}</div>
          <div class="text-[11px] text-slate-400 leading-normal mt-0.5">${item.desc}</div>
        </div>
      </div>
    `).join('');

    resultContainer.innerHTML = `
      <div class="p-6 md:p-8 bg-slate-950 text-white rounded-3xl border border-slate-800 shadow-xl flex flex-col justify-between h-full relative overflow-hidden">
        <!-- Ambient top-right glow -->
        <div class="absolute -top-12 -right-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div>
          <!-- Recommendation Header -->
          <div class="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-slate-800">
            <div class="flex items-center gap-2">
              <span class="text-2xl">${profile.icon}</span>
              <div>
                <span class="text-[11px] font-mono font-semibold text-emerald-400 uppercase tracking-wide">Your Recommended Setup</span>
                <h4 class="font-display font-bold text-lg text-white">${profile.name}</h4>
              </div>
            </div>
            <span class="text-[11px] font-semibold bg-emerald-950 text-emerald-400 border border-emerald-800 px-3 py-1 rounded-full">
              ${badgeText}
            </span>
          </div>

          <!-- Selected Priorities -->
          <div class="py-4 border-b border-slate-800">
            <div class="text-[11px] font-medium text-slate-400 mb-2 uppercase tracking-wider font-mono">Selected Improvements:</div>
            <div class="flex flex-wrap gap-1.5">
              ${goalsPills.length ? goalsPills : '<span class="text-xs text-slate-500">General optimization</span>'}
            </div>
          </div>

          <!-- Included Services Stack -->
          <div class="py-4 space-y-2.5">
            <div class="text-[11px] font-medium text-slate-400 uppercase tracking-wider font-mono">Tailored Solution Architecture:</div>
            <div class="space-y-2">
              ${servicesHtml}
            </div>
          </div>
        </div>

        <!-- Summary & Action Box -->
        <div class="pt-6 mt-4 border-t border-slate-800 space-y-4">
          <div class="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
            <div>
              <div class="text-[11px] text-slate-400 font-medium">Recommended Package</div>
              <div class="font-display font-bold text-lg text-white">${recommendedTier}</div>
              <div class="text-xs text-emerald-400 font-mono mt-0.5">${priceLabel}</div>
            </div>
            <div class="text-right font-mono text-xs text-slate-400">
              <div>Est. Timeline: <span class="text-white font-semibold">${profile.timeline}</span></div>
              <div class="text-[10px] text-slate-500 mt-0.5">${profile.timeSaved}</div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-2.5">
            <button id="config-submit-cta" class="btn bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3.5 px-5 justify-center text-xs flex-1 shadow-md hover:shadow-lg">
              <span>Get My Recommendation →</span>
            </button>
            <a href="https://wa.me/916289485292?text=Hi%20Simplix%20Digital,%20I%20used%20your%20configurator%20for%20a%20${encodeURIComponent(profile.name)}%20and%20would%20like%20to%20discuss." target="_blank" rel="noopener noreferrer" class="btn bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 py-3.5 px-4 justify-center text-xs">
              <span>Talk to Us</span>
            </a>
          </div>

          <p class="text-[11px] text-slate-400 text-center leading-normal">
            No commitment. We'll review your specific requirements before giving a final quote.
          </p>
        </div>
      </div>
    `;

    // Connect modal opening to the recommendation CTA
    const submitBtn = document.getElementById('config-submit-cta');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        const goalsStr = Array.from(state.goals).map(g => GOAL_LABELS[g] || g).join(', ');
        const prefill = `${profile.name} (${recommendedTier}) - Goals: ${goalsStr} - Scale: ${state.scale}`;
        window.openConsultationModal(prefill);
      });
    }
  }

  // Bind Step 1 Type Buttons
  typeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      typeButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const selected = btn.getAttribute('data-type');
      state.type = selected;

      // Sync default goals for business type
      if (BIZ_PROFILES[selected]) {
        state.goals = new Set(BIZ_PROFILES[selected].defaultGoals);
        goalButtons.forEach(gb => {
          const gKey = gb.getAttribute('data-goal');
          if (state.goals.has(gKey)) {
            gb.classList.add('active');
          } else {
            gb.classList.remove('active');
          }
        });
      }

      renderResult();
    });
  });

  // Bind Step 2 Goal Buttons (Multi-select)
  goalButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const goalKey = btn.getAttribute('data-goal');
      if (state.goals.has(goalKey)) {
        if (state.goals.size > 1) {
          state.goals.delete(goalKey);
          btn.classList.remove('active');
        }
      } else {
        state.goals.add(goalKey);
        btn.classList.add('active');
      }
      renderResult();
    });
  });

  // Bind Step 3 Scale Buttons
  scaleButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      scaleButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.scale = btn.getAttribute('data-scale');
      renderResult();
    });
  });

  // Initial render
  renderResult();
}

/**
 * FAQ Accordion with smooth toggle
 */
function initFaqAccordion() {
  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach(item => {
    const trigger = item.querySelector('.faq-trigger');
    if (!trigger) return;

    trigger.addEventListener('click', () => {
      const isActive = item.classList.contains('active');

      // Close all other items
      faqItems.forEach(otherItem => {
        otherItem.classList.remove('active');
        const content = otherItem.querySelector('.faq-content');
        if (content) content.classList.add('hidden');
      });

      // Toggle current
      if (!isActive) {
        item.classList.add('active');
        const content = item.querySelector('.faq-content');
        if (content) content.classList.remove('hidden');
      }
    });
  });
}

/**
 * Modal Handling (Consultation / Project Inquiry)
 */
function initModalHandling() {
  const modal = document.getElementById('consultation-modal');
  const openButtons = document.querySelectorAll('#open-consultation-btn, .hero-cta-btn, .mobile-open-consultation, .package-cta-btn, .open-consultation-link, .case-study-trigger');
  const closeBtn = document.getElementById('close-consultation-modal');
  const form = document.getElementById('project-inquiry-form');
  const successMsg = document.getElementById('modal-success-message');
  const closeSuccessBtn = document.getElementById('close-success-btn');

  if (!modal) return;

  const openModal = (prefillNotes = '') => {
    modal.classList.remove('hidden');
    setTimeout(() => {
      modal.classList.remove('opacity-0');
      const modalInner = modal.querySelector('div');
      if (modalInner) modalInner.classList.remove('scale-95');
    }, 10);

    if (prefillNotes && form) {
      const notesField = document.getElementById('project-notes');
      if (notesField) {
        notesField.value = `Requirements: ${prefillNotes}`;
      }
    }
  };

  const closeModal = () => {
    modal.classList.add('opacity-0');
    const modalInner = modal.querySelector('div');
    if (modalInner) modalInner.classList.add('scale-95');

    setTimeout(() => {
      modal.classList.add('hidden');
      if (form && successMsg) {
        form.classList.remove('hidden');
        successMsg.classList.add('hidden');
        form.reset();
      }
    }, 300);
  };

  window.openConsultationModal = openModal;

  openButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const pkg = btn.getAttribute('data-pkg');
      const study = btn.getAttribute('data-study');
      let note = '';
      if (pkg) note = `${pkg} Package inquiry`;
      else if (study) note = `Case study follow-up: ${study}`;
      openModal(note);
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (closeSuccessBtn) closeSuccessBtn.addEventListener('click', closeModal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      // Simulate successful submission
      form.classList.add('hidden');
      if (successMsg) successMsg.classList.remove('hidden');
    });
  }
}

/**
 * Smooth Anchor Scrolling with dynamic header offset
 */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#' || !targetId) return;

      const targetElem = document.querySelector(targetId);
      if (targetElem) {
        e.preventDefault();
        const headerOffset = 76;
        const elementPosition = targetElem.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });
  });
}
